import { useLazyQuery } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { ButtonDropdown, DropdownItem, DropdownMenu, DropdownToggle, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { IS_REMOVABLE, IS_SCOPE } from '../../../graphql/org-gql';
import useAuth from '../../../hook/useAuth';
import useToggle from '../../../hook/useToggle';
import { LOCALES } from '../../../i18n/locales';
import { messages } from '../../../i18n/message';
import { IOrg } from '../../../types/org';
import CollapsablePanel from '../../common/collapsable-panel/collapsable-panel';
import { AddIcon, CloseIcon, DeleteIcon } from '../../common/icons';
import OrgCreateChildModal from '../org-create/org-create-child';
import OrgCreateOtherModal from '../org-create/org-create-other';
import ExpireOrganization from './expire-organization';
import OrgInformation from './org-information/org-information';
import ParentInformation from './parent-information';
import RelatedOrganizations from './relationship/relationship';
import Button from '../../common/button';

type OrgDetailProps = {
    org: IOrg;
    onOrgSelect?: (org: IOrg | undefined) => void;
}

const OrgDetails = (props: OrgDetailProps) => {
    const {org,onOrgSelect} = props
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false)
    const [initCollapse,setInitCollapse] = useState<boolean>(false);
    const toggleDropdownOpen = useCallback(() => {
        setDropdownOpen(!dropdownOpen)
    }, [dropdownOpen])

    const {isOpen: orgCreateChildOpen, toggle: toggleOrgCreateChild} = useToggle()
    const {isOpen: expireOrgOpen, toggle: toggleExpireOrganization} = useToggle()
    const {isOpen: cannotExpireOrgOpen, toggle: toggleCannotExpireOrganization} = useToggle()
    const {isOpen: expireByOtherOpen, toggle: toggleexpireByOtherOpen} = useToggle()
    const {isOpen: relationshipCreateOpen, toggle: toggleRelationshipCreate} = useToggle()

    const [ isRemovable] = useLazyQuery(IS_REMOVABLE,{fetchPolicy: 'no-cache'});

    const [expirable,setExpirable] = useState<boolean>(false);
    const [ isOrgScope ] = useLazyQuery(IS_SCOPE, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {          
            setExpirable(data?.isScope)           
        }});
    const {isAdmin, isCoordinator, isFrenchTranslator} = useAuth();
    const [cloned,setCloned] = useState<boolean>(false);

    const onExpire =() => {
        if(org){
            isRemovable({variables:{orgId:org?.id}}).then(response=>{
                if(response.data?.isRemovable.success)
                    toggleExpireOrganization()
                else if(response.data?.isRemovable.code==='V1003')
                    toggleexpireByOtherOpen()
                else toggleCannotExpireOrganization()
            })
        }
    }

    useEffect(()=>{
        setCloned(!!org.details?.shareLinkId)
        if(isAdmin) {
            setExpirable(true)
        } else if(isCoordinator) {
            isOrgScope({variables:{orgId:org?.id,isLowerLevel: true}})
        } else {
            setExpirable(false)
        }       
    },[org])

    useEffect(() => {
        // Expand the panel when org changes
        setInitCollapse(false);
    }, [org]);

    useEffect(()=>{
        //console.log('expirable:',expirable)
    },[expirable])



    return (
        <div>
            <CannotExpire isOpen={cannotExpireOrgOpen} message={messages[LOCALES.ENGLISH].INFO['2.2']} toggle={toggleCannotExpireOrganization}/>
            <CannotExpire isOpen={expireByOtherOpen} message={messages[LOCALES.ENGLISH].INFO['2.3']} toggle={toggleexpireByOtherOpen}/>

            {!isFrenchTranslator && <div className="pb-3">
                 <ButtonDropdown isOpen={dropdownOpen} toggle={toggleDropdownOpen} style={{ marginRight: '15px' }} disabled={cloned}>
                    <DropdownToggle caret color="primary" disabled={cloned}>
                        <AddIcon/>
                        &nbsp;<span role="region" aria-label='Create Relationship'>Create Relationship</span>
                    </DropdownToggle>
                    <DropdownMenu style={{ overflowY: 'auto' }}>
                        <DropdownItem onClick={toggleOrgCreateChild}><span role="region" aria-label='Child Organization'>Child Organization</span></DropdownItem>
                        <DropdownItem onClick={toggleRelationshipCreate}><span role="region" aria-label='Other Organization'>Other Organization</span></DropdownItem>
                        {/*<DropdownItem><span role="region" aria-label='Other Position'>Other Position</span></DropdownItem>*/}
                    </DropdownMenu>
                </ButtonDropdown>
                <Button color="danger" style={{ marginRight: '15px' }} onClick={onExpire} disabled={!expirable||cloned}>
                    <DeleteIcon/>
                    &nbsp;<span role="region" aria-label='Expire'>Expire</span>
                </Button>
            </div>
           }
            <CollapsablePanel key={org?.id} headTitle="Organization Information" initCollapse={initCollapse} helpUrl="">
                <OrgInformation org={org} onOrgSelect={onOrgSelect}/>
            </CollapsablePanel>
            <br />
            <CollapsablePanel key={`${org?.id}-parent`} headTitle="Parent Organization" initCollapse={initCollapse} helpUrl="">
                <ParentInformation org={org}/>
            </CollapsablePanel>
            <br />

            <CollapsablePanel key={`${org?.id}-relationships`} headTitle="Relationships to Other Organizations" initCollapse={initCollapse} helpUrl="">
                <RelatedOrganizations org={org}/>
            </CollapsablePanel>

            <OrgCreateChildModal parent={org} isOpen={orgCreateChildOpen} toggle={toggleOrgCreateChild}></OrgCreateChildModal>
            <OrgCreateOtherModal parentOrg={org} isOpen={relationshipCreateOpen} toggle={toggleRelationshipCreate}></OrgCreateOtherModal>
            <ExpireOrganization isOpen={expireOrgOpen} toggle={toggleExpireOrganization} org={org} onOrgSelect={onOrgSelect}/>
        </div>
    )
}

export default OrgDetails

interface CannotExpireProps {
    isOpen: boolean
    message:string
    toggle: () => void
}

export const CannotExpire = (props: CannotExpireProps) => {
    const {isOpen,message, toggle} = props
    return (
        <Modal size="md" isOpen={isOpen} toggle={toggle} >
            <ModalHeader 
                toggle={toggle} 
                tag={'h2'} 
                cssModule={{'modal-title': 'w-100 text-center'}}>
                    Cannot Expire Organization!
            </ModalHeader>
            <ModalBody style={{ textAlign: 'center' }}>
                <p>{message}</p>
            </ModalBody>
            <ModalFooter >
                <Button color="secondary" onClick={toggle}><CloseIcon /> Close</Button>
            </ModalFooter>
        </Modal>
    )
}

